import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const labelOf = (c) => (c.code ? `${c.code} · ${c.name}` : c.name);

// Searchable account/category picker. Props:
//  categories: [{id, code, name, group, type}], value, onChange(id),
//  placeholder, disabled, testid, className
export function AccountCombobox({ categories = [], value, onChange, placeholder = "— choose account —", disabled, testid, className }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => categories.find((c) => c.id === value), [categories, value]);
  const groups = useMemo(() => {
    const order = [];
    const map = {};
    for (const c of categories) {
      const g = c.group || "Other";
      if (!map[g]) { map[g] = []; order.push(g); }
      map[g].push(c);
    }
    return order.map((g) => ({ group: g, items: map[g] }));
  }, [categories]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled} data-testid={testid}
          className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground", className)}>
          <span className="truncate">{selected ? labelOf(selected) : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={(val, search) => (val.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Search code or name…" data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty>No account found.</CommandEmpty>
            {groups.map(({ group, items }) => (
              <CommandGroup key={group} heading={group}>
                {items.map((c) => (
                  <CommandItem key={c.id} value={`${c.code || ""} ${c.name} ${group}`}
                    onSelect={() => { onChange(c.id); setOpen(false); }} data-testid={testid ? `${testid}-opt-${c.id}` : undefined}>
                    <Check className={cn("mr-2 h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{labelOf(c)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
